import Banner from '@sats-group/ui-lib/react/banner';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import React, { useRef, useState } from 'react';
import { type Core, useAdyen } from '~/client/hooks/use-adyen';
import useAsyncEffect from '~/client/hooks/use-async-effect';
import { replaceQueryParameters } from '~/shared/replace-query-parameters';
import Spinner from '../spinner/spinner';
import type { PaymentV2 as Props } from './payment-v2.types';

const PaymentV2: React.FC<Props> = ({
  actions,
  messages,
  payment,
  session,
}) => {
  const checkoutRef = useRef<Core>(null);
  const dropinContainer = useRef<HTMLDivElement>(null);
  const [localMessages, setLocalMessages] = useState<
    Parameters<typeof Banner>[0][]
  >([]);
  const handleAdyenLoadingError = () =>
    setLocalMessages([messages.errorLoadingComponent]);
  const [isAdyenLoading, hasAdyenLoaded] = useAdyen(handleAdyenLoadingError);

  useAsyncEffect(async () => {
    if (
      !dropinContainer.current ||
      !hasAdyenLoaded ||
      !session ||
      !window.AdyenWeb ||
      isAdyenLoading
    ) {
      return;
    }

    setLocalMessages([]);

    const { AdyenCheckout, Dropin } = window.AdyenWeb;

    checkoutRef.current = await AdyenCheckout({
      clientKey: payment.clientKey,
      environment: payment.environment,
      locale: payment.locale,
      countryCode: payment.locale.split('-')[1],
      onError: () => {
        setLocalMessages([messages.errorCheckout]);
        if (checkoutRef.current) {
          checkoutRef.current.update(checkoutRef.current.options);
        }
      },
      onPaymentCompleted: response => {
        setLocalMessages([messages.paymentComplete[response.resultCode]]);

        switch (response.resultCode) {
          case 'Authorised': {
            const staleHref = replaceQueryParameters(window.location.href, {
              [payment.staleKey]: payment.staleKey,
            });

            window.history.replaceState(null, document.title, staleHref);
            window.location.assign(payment.nextHref);

            break;
          }
          default: {
            if (checkoutRef.current) {
              checkoutRef.current.update(checkoutRef.current.options);
            }
          }
        }
      },
      session,
    });

    new Dropin(checkoutRef.current, {
      openFirstPaymentMethod: true,
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
          challengeWindowSize: '05',
          name: payment.cardName,
        },
      },
      showRadioButton: true,
    }).mount(dropinContainer.current);
  }, [isAdyenLoading, hasAdyenLoaded, payment, session]);

  return (
    <div className="payment-v2">
      <noscript>
        <Banner {...messages.noscript} />
      </noscript>
      <div className="payment-v2__messages">
        {localMessages.map(entry => (
          <div key={entry.text}>
            <Banner {...entry} />
          </div>
        ))}
      </div>
      {isAdyenLoading ? <Spinner theme={Spinner.themes.centered} /> : null}
      <div ref={dropinContainer} />
      <div className="payment-v2__actions">
        {actions.map(entry => (
          <div key={entry.href}>
            <LinkButton {...entry} variant={LinkButton.variants.secondary} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentV2;
